import axios from "axios";
import type { BillingAddress, BillingAddressPayload } from "@/interfaces/billingAddresses";
import { apiClient } from "@/lib/apiClient";

function rethrowApiError(error: unknown, fallback: string): never {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    throw new Error(error.response.data.error as string);
  }
  throw error instanceof Error ? error : new Error(fallback);
}

const base = (groupId: number) => `/admin/groups/${groupId}/billing-addresses`;

export async function fetchBillingAddresses(groupId: number): Promise<BillingAddress[]> {
  const { data } = await apiClient.get<{ success: boolean; data: BillingAddress[] }>(base(groupId));
  return data.data;
}

export async function createBillingAddress(
  groupId: number,
  payload: BillingAddressPayload,
): Promise<BillingAddress> {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: BillingAddress }>(
      base(groupId),
      payload,
    );
    return data.data;
  } catch (error) {
    rethrowApiError(error, "Couldn't save the address.");
  }
}

/**
 * Removes an address from the group's book. The row is kept and flagged inactive, so invoices that
 * reference it are untouched and its label becomes available again.
 */
export async function archiveBillingAddress(groupId: number, addressId: number): Promise<void> {
  try {
    await apiClient.delete(`${base(groupId)}/${addressId}`);
  } catch (error) {
    rethrowApiError(error, "Couldn't remove the address.");
  }
}
